import {
  Injectable,
  Injector,
  InjectionToken,
  inject,
  signal
} from '@angular/core';
import { Location } from '@angular/common';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { throttleTime } from 'rxjs';
import { RestErrorHandlerService } from '../../services/rest/rest-error-handler.service';
import { SystemErrorComponent } from './system-error.component';
import type { OnDestroy, ComponentRef, WritableSignal } from '@angular/core';
import type { OverlayRef } from '@angular/cdk/overlay';
import type { Subscription } from 'rxjs';
import type { PostBootstrapInit } from '../../services/post-bootstrap-init';

export type CloseSystemError = () => void;

/** Injection token for closing system error. */
export const SYSTEM_ERROR_CLOSE = new InjectionToken<CloseSystemError>('systemErrorCloseFunction');

/**
 * A service to open system error messages.
 */
@Injectable({
  providedIn: 'root'
})
export class SystemErrorService implements OnDestroy, PostBootstrapInit {
  private static readonly DEBOUNCE_DELAY_MS: number = 500;

  private readonly overlay: Overlay = inject(Overlay);
  private readonly injector: Injector = inject(Injector);
  private readonly location: Location | null = inject(Location, { optional: true });
  private readonly restErrorHandlerService: RestErrorHandlerService = inject(RestErrorHandlerService);

  /** Open state for a system error message */
  private readonly isOpen: WritableSignal<boolean> = signal(false);

  /** Error subscription */
  private onErrorSubscription: Subscription;

  /** Overlay Reference */
  private overlayRef: OverlayRef;

  /**
   * The constructor of the component.
   * Subscribes to rest errors and opens a system error message
   */
  constructor() {
    if (this.location !== null) {
      this.location.subscribe((): void => this.close());
    }
  }

  /**
   * Registers to rest error handler and opens system error when there is an error
   */
  init(): void {
    if (this.onErrorSubscription === undefined) {
      this.onErrorSubscription = this.restErrorHandlerService.onError
        .pipe(
          throttleTime(SystemErrorService.DEBOUNCE_DELAY_MS)
        )
        .subscribe((): void => this.open());
    }
  }

  /**
   * Cleans up subscription and closes opened system error
   */
  ngOnDestroy(): void {
    if (this.onErrorSubscription !== undefined) {
      this.onErrorSubscription.unsubscribe();
    }
    if (this.isOpen()) {
      this.close();
    }
  }

  /**
   * Closes system error
   */
  close(): void {
    if (this.overlayRef !== undefined) {
      this.overlayRef.dispose();
      this.overlayRef = undefined;
      this.isOpen.set(false);
    }
  }

  /**
   * Opens system error
   */
  open(): void {
    if (this.isOpen()) {
      this.close();
    }
    setTimeout((): void => {
      this.overlayRef = this.createOverlay();
      this.attachSystemError(this.overlayRef);
      this.isOpen.set(true);
    }, 0);
  }

  /**
   * Creates the overlay into which the dialog will be loaded.
   *
   * @returns A promise resolving to the OverlayRef for the created overlay.
   */
  private createOverlay(): OverlayRef {
    return this.overlay.create(this.getOverlayConfig());
  }

  /**
   * @returns The overlay configuration.
   */
  private getOverlayConfig(): OverlayConfig {
    return new OverlayConfig({
      panelClass: 'td-wb-overlay-panel-fixed-position',
      positionStrategy: this.overlay
        .position()
        .global()
        .centerHorizontally()
        .bottom('0'),
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      hasBackdrop: false,
      width: '100%'
    });
  }

  /**
   * Attaches system error component to the overlay
   *
   * @returns SystemErrorComponent
   */
  private attachSystemError(overlayRef: OverlayRef): SystemErrorComponent {
    const injector = this.createInjector();
    const containerPortal = new ComponentPortal(
      SystemErrorComponent,
      null,
      injector
    );
    const containerRef: ComponentRef<SystemErrorComponent> = overlayRef.attach(
      containerPortal
    );
    return containerRef.instance;
  }

  /**
   * Creates and returns injector for system error component
   *
   * @returns Injector
   */
  private createInjector(): Injector {
    return Injector.create({
      parent: this.injector,
      providers: [{ provide: SYSTEM_ERROR_CLOSE, useValue: (): void => this.close() }],
      name: 'SystemErrorInjector'
    });
  }
}
